import Keycloak from "keycloak-js"

import { setAccountInfo } from "./store/reducers/accountInfo"

/**
 * Keycloak-koppeling, bedoeld om ongewijzigd tussen projecten te delen. Of inloggen verplicht is
 * bepaalt de app zelf, in App.js:
 *
 * * `checkSSOSession()` → `Promise<boolean>`. Kijkt stil of er al een sessie is. Redirect nooit en
 *   gooit nooit; `false` betekent geen sessie, of controle niet afgerond.
 * * `getAccessToken()` → stuurt naar de inlogpagina, tenzij al ingelogd.
 * * `logout()` → stopt het verversen en gaat via Keycloak uit.
 *
 * Gaat uit van een config-object. Bij een `oidcProvider` of JSON-config-URL worden de endpoints
 * asynchroon opgehaald en kan `getAccessToken()` daar vlak na het opstarten mee racen.
 */

const keycloak = new Keycloak({
  url: process.env.REACT_APP_KEYCLOAK_URL,
  realm: process.env.REACT_APP_KEYCLOAK_REALM,
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID
})

let initPromise = null
let refreshTimeout = null

// Een instantie is maar één keer te initialiseren, dus alle aanroepers delen deze promise.
const initKeycloak = (options) => {
  if (!initPromise) {
    initPromise = keycloak.init({
      pkceMethod: "S256",
      checkLoginIframe: false,
      ...options,
    })
  }
  return initPromise
}

const updateAccountInfo = (dispatch) => {
  dispatch(setAccountInfo({
    name: keycloak.tokenParsed.name,
    email: keycloak.tokenParsed.email,
    email_verified: keycloak.tokenParsed.email_verified,
    token: keycloak.token,
    // `sub` is het gebruikers-id in Keycloak; nodig om je eigen account te herkennen.
    id: keycloak.tokenParsed.sub,
    roles: keycloak.realmAccess?.roles || [],
    // De rollen per client, zoals die van `realm-management`. Die staan niet in `realm_access` en
    // waren dus onzichtbaar voor de app, terwijl er wel rechten in zitten.
    resourceAccess: keycloak.resourceAccess || {}
  }))
}

// Seconden vóór het verlopen dat we het token verversen.
const REFRESH_MARGIN = 30

const scheduleTokenRefresh = (dispatch) => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout)
    refreshTimeout = null
  }

  const expiresIn = keycloak.tokenParsed.exp - Math.ceil(Date.now() / 1000)
  const refreshIn = Math.max((expiresIn - REFRESH_MARGIN) * 1000, 5000)

  refreshTimeout = setTimeout(() => {
    // minValidity moet ruimer zijn dan REFRESH_MARGIN, anders is het token op dit moment nog
    // precies geldig genoeg en ververst updateToken niets.
    keycloak.updateToken(REFRESH_MARGIN + 30)
      .then((refreshed) => {
        if (refreshed) updateAccountInfo(dispatch)
        scheduleTokenRefresh(dispatch)
      })
      .catch(() => {
        console.warn("Token refresh failed, redirecting to login")
        keycloak.login()
      })
  }, refreshIn)
}

// keycloak-js zet géén timeout op zijn silent-check-iframe: komt er geen postMessage terug, dan
// blijft init() hangen en daarmee de hele app op zijn laadscherm.
const SILENT_CHECK_TIMEOUT = 8000

const withTimeout = (promise, ms) => Promise.race([
  promise,
  new Promise((resolve, reject) => setTimeout(() => reject(new Error("Silent SSO check timed out")), ms))
])

// De stille controle gebruikt een iframe op de Keycloak-server en werkt alleen als de browser daar
// third-party cookies naartoe stuurt. Op localhost is Keycloak een andere site, en Brave
// partitioneert die cookies: de iframe ziet de sessie dan niet, een top-level redirect wel.
// Zonder deze URI doet check-sso zo'n redirect, met prompt=none, dus geen inlogscherm voor wie
// niet is ingelogd, alleen een korte omleiding bij het laden.
const isLocalhost = ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname)

const silentCheckSsoRedirectUri = isLocalhost
  ? undefined
  : window.location.origin + process.env.PUBLIC_URL + "/silent-check-sso.html"

/**
 * Staat Keycloak op een andere site dan de app?
 *
 * Dat is precies het verschil tussen de twee omgevingen: de testomgeving deelt zijn domein met
 * Keycloak (beide `hkvservices.nl`, dus first-party), de productiesite niet (`eggspedition.org`
 * naast `hkvservices.nl`). Alleen in dat tweede geval is het cookie van de iframe een third-party
 * cookie, en die gooien Opera, Brave en Safari weg. Vandaar dat de stille controle op test werkt en
 * op productie niet, en dat een top-level redirect dezelfde sessie wél vindt.
 *
 * "Dezelfde site" is hier de laatste twee labels van de hostnaam. Goed genoeg voor `.nl` en `.org`;
 * bij een domein als `co.uk` valt het te ruim uit, en dan is dit alleen een gemiste optimalisatie.
 */
const registrableDomain = (hostname) => String(hostname || "").split(".").slice(-2).join(".")

const keycloakHostname = (() => {
  try {
    return new URL(process.env.REACT_APP_KEYCLOAK_URL).hostname
  } catch (_) {
    return ""
  }
})()

const isCrossSiteKeycloak = Boolean(keycloakHostname)
  && registrableDomain(keycloakHostname) !== registrableDomain(window.location.hostname)

// Deze tab heeft de omleiding hieronder al gedaan. In `sessionStorage`, zodat het per tab geldt en
// niet blijft plakken. Privémodus kan opslag weigeren, en dan gaat de omleiding niet door: zonder
// vlag is er niets dat een tweede ronde tegenhoudt.
const SSO_REDIRECT_KEY = "keycloak.sso.redirect"

const ssoRedirectTried = () => {
  try {
    return window.sessionStorage.getItem(SSO_REDIRECT_KEY) === "1"
  } catch (_) {
    return true
  }
}

// Zet de vlag en leest hem terug: alleen als hij er echt staat, is de omleiding veilig.
const markSsoRedirectTried = () => {
  try {
    window.sessionStorage.setItem(SSO_REDIRECT_KEY, "1")
    return window.sessionStorage.getItem(SSO_REDIRECT_KEY) === "1"
  } catch (_) {
    return false
  }
}

/**
 * De stille controle zag geen sessie. Staat Keycloak op een andere site, dan kón die iframe het ook
 * niet zien: dat is geen antwoord maar een blinde vlek. Het echte antwoord halen we dan op met een
 * top-level redirect met `prompt=none` — first-party, dus ook met Brave's Shields aan, en zonder
 * inlogscherm: wie geen sessie heeft komt meteen terug met `login_required`.
 *
 * Eén keer per tab, anders zou juist die terugkomst een volgende omleiding uitlokken.
 */
const shouldRetrySsoWithRedirect = () => {
  const tried = ssoRedirectTried()

  // Eén regel die zegt waaróm de terugval wel of niet gebeurt: zonder dit is er aan de buitenkant
  // geen verschil tussen "geen sessie" en "de sessie niet kunnen zien".
  if (!isCrossSiteKeycloak || isLocalhost || tried) {
    console.warn("SSO restore skipped", { crossSite: isCrossSiteKeycloak, tried })
    return false
  }

  return true
}

const retrySsoWithRedirect = () => {
  if (!markSsoRedirectTried()) {
    console.warn("SSO restore skipped: no session storage")
    return
  }

  try {
    keycloak.login({ prompt: "none", redirectUri: window.location.href })
  } catch (error) {
    // Bij een afgekapte init is de adapter er misschien nog niet. Dan blijft de app gewoon
    // uitgelogd staan; de inlogknop werkt nog.
    console.warn("SSO redirect retry failed", error)
  }
}

export const checkSSOSession = () => async (dispatch) => {
  try {
    const authenticated = await withTimeout(initKeycloak({
      onLoad: "check-sso",
      silentCheckSsoRedirectUri,
    }), SILENT_CHECK_TIMEOUT)

    if (!authenticated) {
      if (shouldRetrySsoWithRedirect()) retrySsoWithRedirect()
      return false
    }

    updateAccountInfo(dispatch)
    scheduleTokenRefresh(dispatch)

    return true
  } catch (error) {
    console.warn("SSO check error", error)

    // Ook een vastgelopen iframe is een blinde vlek: juist een browser die het antwoord tegenhoudt
    // laat de controle hangen tot de timeout.
    if (shouldRetrySsoWithRedirect()) retrySsoWithRedirect()

    return false
  }
}

// Wacht bewust niet op initKeycloak: een vastgelopen of afgekapte stille controle blijft dat, en
// zou deze knop dood maken. login() is enkel een redirect; na terugkomst initialiseert de app zich
// alsnog, dan met de callback in de URL.
export const getAccessToken = () => async (dispatch) => {
  try {
    if (keycloak.authenticated) {
      updateAccountInfo(dispatch)
      scheduleTokenRefresh(dispatch)
      return keycloak.token
    }

    return keycloak.login()
  } catch (error) {
    console.warn("Login error", error)
  }
}

export const logout = () => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout)
    refreshTimeout = null
  }

  // Uitloggen is een top-level redirect naar Keycloak en terug; dat is dezelfde controle als de
  // terugval hieronder, dus die hoeft in deze tab niet nog eens.
  markSsoRedirectTried()

  keycloak.logout({
    redirectUri: window.location.href
  })
}
